import React, { useCallback, useEffect, useMemo, useState } from 'react';
import StaffNavbar from '../components/StaffNavbar';
import StaffRewardImageUpload from '../components/StaffRewardImageUpload';
import apiService from '../services/api';
import '../styles/StaffPages.css';
import xmarkIcon from '../assets/icons/xmark.svg';

const ghostAddButton = {
  padding: '6px 12px',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  backgroundColor: 'white',
  fontFamily: '"Baikal Book"',
  fontSize: '13px',
  color: '#374151',
  cursor: 'pointer',
};

const prizeCardGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: '16px',
  alignItems: 'start',
  marginBottom: '16px',
};

const prizeMenuItem = {
  display: 'block',
  width: '100%',
  padding: '8px 12px',
  backgroundColor: 'transparent',
  color: '#374151',
  border: 'none',
  fontFamily: '"Baikal Book"',
  fontSize: '14px',
  cursor: 'pointer',
  textAlign: 'left',
};

const createEmptyForm = ({
  defaultDescription,
  defaultCoverTitle,
  dateStartKey,
  dateEndKey,
  dateStartTimeKey,
  dateEndTimeKey,
  defaultStartTime,
  defaultEndTime,
}) => ({
  reward: '',
  title: '$25 Gift Card',
  giveaway_type: 'gift_card',
  description: defaultDescription,
  how_to_win: '',
  location: '',
  terms: '',
  cover_title: defaultCoverTitle,
  cover_image: '',
  [dateStartKey]: '',
  [dateEndKey]: '',
  [dateStartTimeKey]: defaultStartTime,
  [dateEndTimeKey]: defaultEndTime,
  is_active: true,
});

const defaultWeeklyApiMethods = {
  get: 'getWeeklyPrizes',
  create: 'createWeeklyPrize',
  update: 'updateWeeklyPrize',
  delete: 'deleteWeeklyPrize',
};

const StaffWeeklyRewards = ({
  activeSection = 'weekly-rewards',
  pageTitle = 'Weekly Rewards',
  pageSubtitle = 'Configure weekly leaderboard reward for your school.',
  loadingLabel = 'Loading weekly rewards...',
  emptyActiveLabel = 'No active weekly rewards.',
  emptyAllLabel = 'No weekly rewards created yet.',
  emptyCompletedLabel = 'No completed weekly rewards yet.',
  deleteConfirmLabel = 'Delete this weekly reward?',
  loadErrorLabel = 'Failed to load weekly rewards.',
  deleteErrorLabel = 'Failed to delete weekly reward.',
  saveErrorLabel = 'Failed to save weekly reward.',
  formCreateTitle = 'Add Weekly Reward',
  formEditTitle = 'Update Weekly Reward',
  formCreateSubtitle = 'Create a weekly reward for leaderboard winners.',
  formEditSubtitle = 'Update weekly reward settings.',
  termsPlaceholder = 'Weekly reward terms shown in app',
  defaultCoverTitle = 'Weekly reward',
  defaultDescription = 'Top points earner in your school wins this week.',
  coverTitlePlaceholder = 'Weekly reward',
  startDateLabel = 'Week Start Date',
  endDateLabel = 'Week End Date',
  startEndRequiredError = 'Set both week start and week end dates.',
  endBeforeStartError = 'Week end date cannot be earlier than week start date.',
  overlapError = 'Date range overlaps with an existing weekly reward.',
  dateStartKey = 'week_start_date',
  dateEndKey = 'week_end_date',
  dateStartTimeKey = 'week_start_time',
  dateEndTimeKey = 'week_end_time',
  // The authoritative UTC instants. Everything shown and edited is derived from
  // these in the viewer's own timezone; the naive keys above are the fallback
  // for rows saved before the schedule moved to UTC.
  dateStartAtKey = 'week_start_at',
  dateEndAtKey = 'week_end_at',
  defaultStartTime = '00:00',
  defaultEndTime = '16:00',
  startTimeLabel = 'Start time',
  endTimeLabel = 'End time',
  autoFillNextRange = true,
  usesCombinedDateTime = false,
  apiMethods = defaultWeeklyApiMethods,
}) => {
  const [isSuperuser, setIsSuperuser] = useState(() =>
    Boolean(apiService.getCurrentUser()?.is_superuser)
  );
  const [showAddForm, setShowAddForm] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const emptyFormArgs = {
    defaultDescription,
    defaultCoverTitle,
    dateStartKey,
    dateEndKey,
    dateStartTimeKey,
    dateEndTimeKey,
    defaultStartTime,
    defaultEndTime,
  };
  const [form, setForm] = useState(() => createEmptyForm(emptyFormArgs));
  const [overrideCoverTitle, setOverrideCoverTitle] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [leaderboardRewardImages, setLeaderboardRewardImages] = useState([]);
  const [leaderboardImageInputKey, setLeaderboardImageInputKey] = useState(0);
  // Gallery rows already saved against the prize being edited, so the form can
  // offer them for removal alongside any new uploads.
  const [editingRewardImages, setEditingRewardImages] = useState([]);
  // Ids of already-saved gallery images the user removed in the open edit form.
  const [removedLeaderboardImageIds, setRemovedLeaderboardImageIds] = useState([]);
  const [expandedLeaderboardItems, setExpandedLeaderboardItems] = useState({});
  // Which prize card has its ⋮ menu open, by id.
  const [openItemMenu, setOpenItemMenu] = useState(null);
  const [prizeLeaderboards, setPrizeLeaderboards] = useState({});
  const [prizeLeaderboardLoading, setPrizeLeaderboardLoading] = useState({});
  const [prizeLeaderboardErrors, setPrizeLeaderboardErrors] = useState({});
  const [participantModal, setParticipantModal] = useState({
    open: false,
    prizeId: null,
    prizeTitle: '',
    participant: null,
    period: null,
  });
  const [participantLoading, setParticipantLoading] = useState(false);
  const [participantError, setParticipantError] = useState('');

  const formatApiError = useCallback((data, fallbackMessage) => {
    const toTitle = key =>
      String(key)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase());

    const collectMessages = (value, parentKey = '') => {
      if (value === null || value === undefined || value === '') return [];
      if (typeof value === 'string') {
        return [parentKey ? `${toTitle(parentKey)}: ${value}` : value];
      }
      if (typeof value === 'number' || typeof value === 'boolean') {
        return [parentKey ? `${toTitle(parentKey)}: ${value}` : String(value)];
      }
      if (Array.isArray(value)) {
        return value.flatMap(item => collectMessages(item, parentKey));
      }
      if (typeof value === 'object') {
        return Object.entries(value).flatMap(([key, nestedValue]) => {
          const nextKey =
            key === 'non_field_errors' || key === 'detail' || key === 'error'
              ? ''
              : key;
          return collectMessages(nestedValue, nextKey);
        });
      }
      return [parentKey ? `${toTitle(parentKey)}: ${String(value)}` : String(value)];
    };

    const messages = collectMessages(data);
    return messages.length ? Array.from(new Set(messages)).join('\n') : fallbackMessage;
  }, []);

  const normalizeDateInput = value => {
    if (!value) return '';
    const raw = String(value);
    const datePart = raw.includes('T') ? raw.split('T')[0] : raw;
    const date = new Date(`${datePart}T00:00:00`);
    if (Number.isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const normalizeTimeInput = value => {
    if (!value) return '';
    const raw = String(value);
    if (/^\d{2}:\d{2}/.test(raw)) {
      return raw.slice(0, 5);
    }
    if (raw.includes('T')) {
      return normalizeTimeInput(raw.split('T')[1]);
    }
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return '';
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const combineDateTime = (dateStr, timeStr, fallbackTime = '00:00') => {
    if (!dateStr) return '';
    const time = normalizeTimeInput(timeStr) || fallbackTime;
    return `${dateStr}T${time}:00`;
  };

  const pad2 = value => String(value).padStart(2, '0');

  /** A stored UTC instant as the YYYY-MM-DD this browser would call it. */
  const localDateFromInstant = value => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  };

  /** The same instant as the HH:MM this browser would call it. */
  const localTimeFromInstant = value => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  };

  /**
   * The reverse: what the admin picked in their own timezone, as a UTC instant.
   * `new Date('YYYY-MM-DDTHH:MM:00')` with no offset is parsed as local time,
   * which is exactly the reading we want here.
   */
  const instantFromLocal = (dateStr, timeStr, fallbackTime = '00:00') => {
    if (!dateStr) return null;
    const time = normalizeTimeInput(timeStr) || fallbackTime;
    const d = new Date(`${dateStr}T${time}:00`);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  };

  /** Local date + time for an item, preferring its UTC instant. */
  const itemLocalDate = (item, atKey, dateKey) =>
    localDateFromInstant(item?.[atKey]) || normalizeDateInput(item?.[dateKey]);

  const itemLocalTime = (item, atKey, timeKey, dateKey) =>
    localTimeFromInstant(item?.[atKey]) || getItemScheduleTime(item, timeKey, dateKey);

  const getItemScheduleDate = item => normalizeDateInput(item?.[dateStartKey]);

  const getItemScheduleTime = (item, timeKey, dateKey) => {
    if (item?.[timeKey]) {
      return normalizeTimeInput(item[timeKey]);
    }
    const raw = item?.[dateKey];
    if (raw && String(raw).includes('T')) {
      return normalizeTimeInput(raw);
    }
    return '';
  };

  /**
   * A schedule bound rendered in the viewer's own timezone.
   *
   * Reads the UTC instant when the row has one; rows saved before the schedule
   * moved to UTC fall back to their naive columns, which are school-local and so
   * are shown as-is rather than pretending to be converted.
   */
  const formatScheduleCell = (item, dateKey, timeKey, atKey) => {
    const instant = atKey ? item?.[atKey] : null;
    if (instant) {
      const d = new Date(instant);
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          ...(isSuperuser ? { hour: 'numeric', minute: '2-digit' } : {}),
        });
      }
    }
    const dateValue = normalizeDateInput(item?.[dateKey]);
    if (!dateValue) return '—';
    if (!isSuperuser) return dateValue;
    const timeValue = getItemScheduleTime(item, timeKey, dateKey);
    return timeValue ? `${dateValue} ${timeValue}` : dateValue;
  };

  const getSubmitStartTime = (existingItem = null) => {
    if (isSuperuser) {
      return form[dateStartTimeKey] || defaultStartTime;
    }
    if (existingItem) {
      return getItemScheduleTime(existingItem, dateStartTimeKey, dateStartKey) || defaultStartTime;
    }
    return defaultStartTime;
  };

  const getSubmitEndTime = (existingItem = null) => {
    if (isSuperuser) {
      return form[dateEndTimeKey] || defaultEndTime;
    }
    if (existingItem) {
      return getItemScheduleTime(existingItem, dateEndTimeKey, dateEndKey) || defaultEndTime;
    }
    return defaultEndTime;
  };

  /**
   * An item's window as real instants, so the overlap check compares like with like.
   *
   * The form being submitted is built from the stored UTC instant (see itemLocalDate /
   * itemLocalTime), so reading the naive mirror columns here compared a UTC-derived range
   * against a wall clock the browser assumed was local — the two disagreed by the server's
   * offset and rejected legitimate times as overlapping. Prefer the instant, exactly as
   * formatScheduleCell does; the naive pair stays as the fallback for rows written before
   * the *_at columns existed.
   */
  const rangeMsFrom = (item, atKey, dateKey, timeKey, fallbackTime) => {
    const instant = item?.[atKey];
    if (instant) {
      const ms = new Date(instant).getTime();
      if (!Number.isNaN(ms)) return ms;
    }
    const dateValue = normalizeDateInput(item?.[dateKey]);
    if (!dateValue) return null;
    const timeValue = getItemScheduleTime(item, timeKey, dateKey) || fallbackTime;
    return new Date(combineDateTime(dateValue, timeValue)).getTime();
  };

  const getRangeStartMs = item =>
    rangeMsFrom(item, dateStartAtKey, dateStartKey, dateStartTimeKey, defaultStartTime);

  const getRangeEndMs = item =>
    rangeMsFrom(item, dateEndAtKey, dateEndKey, dateEndTimeKey, defaultEndTime);

  const addDays = (dateStr, days) => {
    const date = new Date(`${dateStr}T00:00:00`);
    date.setDate(date.getDate() + days);
    return normalizeDateInput(date.toISOString());
  };

  const getCycleLengthDays = source => {
    const sourceStart = normalizeDateInput(source?.[dateStartKey]);
    const sourceEnd = normalizeDateInput(source?.[dateEndKey]);
    if (!sourceStart || !sourceEnd) return 6;
    const start = new Date(`${sourceStart}T00:00:00`);
    const end = new Date(`${sourceEnd}T00:00:00`);
    const diff = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    return Number.isFinite(diff) && diff >= 0 ? diff : 6;
  };

  const sortedItems = useMemo(
    () =>
      [...items].sort((a, b) => {
        const aStart = normalizeDateInput(a?.[dateStartKey]);
        const bStart = normalizeDateInput(b?.[dateStartKey]);
        if (!aStart) return 1;
        if (!bStart) return -1;
        return aStart < bStart ? 1 : -1;
      }),
    [items, dateStartKey]
  );
  const activeItems = useMemo(() => sortedItems.filter(item => item.is_active), [sortedItems]);
  const completedItems = useMemo(() => sortedItems.filter(item => !item.is_active), [sortedItems]);

  // Saved gallery rows still standing after any removals staged in this form.
  const savedRewardImages = useMemo(
    () =>
      editingRewardImages.filter(
        img => !removedLeaderboardImageIds.includes(String(img.id))
      ),
    [editingRewardImages, removedLeaderboardImageIds]
  );

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const nowMs = Date.now();

  const getLifecycleStatus = useCallback(item => {
    if (!item?.is_active) return 'completed';
    const startMs = getRangeStartMs(item);
    const endMs = getRangeEndMs(item);
    if (startMs === null || endMs === null) return 'unknown';
    if (nowMs < startMs) return 'upcoming';
    if (nowMs > endMs) return 'ended_pending_finalize';
    return 'live';
  }, [
    dateEndKey,
    dateEndTimeKey,
    dateStartKey,
    dateStartTimeKey,
    defaultEndTime,
    defaultStartTime,
    nowMs,
    usesCombinedDateTime,
  ]);

  const getDaysUntilEnd = item => {
    const endMs = getRangeEndMs(item);
    if (endMs === null) return null;
    const deltaMs = endMs - nowMs;
    const days = Math.ceil(deltaMs / (1000 * 60 * 60 * 24));
    return Number.isFinite(days) ? days : null;
  };

  const getStatusMeta = item => {
    const lifecycle = getLifecycleStatus(item);
    if (lifecycle === 'completed') {
      return { label: 'Completed', bg: '#EEF2F7', color: '#334155' };
    }
    if (lifecycle === 'upcoming') {
      return { label: 'Upcoming', bg: '#EFF6FF', color: '#1D4ED8' };
    }
    if (lifecycle === 'live') {
      const daysLeft = getDaysUntilEnd(item);
      return {
        label: daysLeft !== null ? `Live • ${Math.max(0, daysLeft)}d left` : 'Live',
        bg: '#ECFDF3',
        color: '#047857',
      };
    }
    if (lifecycle === 'ended_pending_finalize') {
      return { label: 'Live', bg: '#ECFDF3', color: '#047857' };
    }
    return { label: item?.is_active ? 'Active' : 'Inactive', bg: '#F3F4F6', color: '#4B5563' };
  };

  const matchesSearch = useCallback(item => {
    if (!normalizedQuery) return true;
    return [
      item.title,
      item.description,
      item.winner_name,
      item.winner_email,
      item[dateStartKey],
      item[dateEndKey],
    ]
      .filter(Boolean)
      .some(value => String(value).toLowerCase().includes(normalizedQuery));
  }, [dateEndKey, dateStartKey, normalizedQuery]);

  const passesStatusFilter = useCallback(item => {
    if (statusFilter === 'all') return true;
    return getLifecycleStatus(item) === statusFilter;
  }, [getLifecycleStatus, statusFilter]);

  const visibleActiveItems = useMemo(
    () => activeItems.filter(item => matchesSearch(item) && passesStatusFilter(item)),
    [activeItems, matchesSearch, passesStatusFilter]
  );
  const visibleCompletedItems = useMemo(
    () => completedItems.filter(item => matchesSearch(item) && passesStatusFilter(item)),
    [completedItems, matchesSearch, passesStatusFilter]
  );

  const getImageUrl = imagePath => {
    if (!imagePath) return null;
    if (typeof imagePath !== 'string') return null;
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;
    if (imagePath.startsWith('s3://app.gethomecrowd.com/')) {
      return imagePath.replace('s3://app.gethomecrowd.com/', 'https://app.gethomecrowd.com/');
    }
    if (imagePath.startsWith('s3://')) {
      return imagePath.replace('s3://', 'https://');
    }
    const apiBaseRaw =
      process.env.REACT_APP_API_BASE_URL ||
      (process.env.NODE_ENV === 'production'
        ? 'https://api.gethomecrowd.com/api'
        : 'http://localhost:8000/api');
    const apiBase = apiBaseRaw.replace(/\/+$/, '');
    const apiOrigin = apiBase.endsWith('/api') ? apiBase.slice(0, -4) : apiBase;
    if (imagePath.startsWith('/')) {
      return `${apiOrigin}${imagePath}`;
    }
    return `${apiOrigin}/${imagePath}`;
  };

  const getItemPreviewImages = item => {
    const candidates = [
      // The prize serializer resolves this from its leaderboard reward's gallery.
      ...(Array.isArray(item?.images) ? item.images : []).flatMap(img => [
        img?.image_url,
        img?.image_path,
      ]),
      item?.cover_image_url,
      item?.cover_image,
      item?.reward_image_url,
      item?.reward_image,
      item?.image_url,
    ];

    return Array.from(new Set(candidates.map(getImageUrl).filter(Boolean)));
  };

  const getAutoDates = () => {
    if (!autoFillNextRange || !sortedItems.length) return { [dateStartKey]: '', [dateEndKey]: '' };
    const latestByEnd = [...sortedItems].sort((a, b) =>
      normalizeDateInput(a?.[dateEndKey]) < normalizeDateInput(b?.[dateEndKey]) ? 1 : -1
    )[0];
    const firstByStart = [...sortedItems].sort((a, b) =>
      normalizeDateInput(a?.[dateStartKey]) > normalizeDateInput(b?.[dateStartKey]) ? 1 : -1
    )[0];
    const cycleLength = getCycleLengthDays(firstByStart);
    const nextStart = addDays(normalizeDateInput(latestByEnd?.[dateEndKey]), 1);
    const nextEnd = addDays(nextStart, cycleLength);
    return { [dateStartKey]: nextStart, [dateEndKey]: nextEnd };
  };

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      setPageError('');
      const resp = await apiService[apiMethods.get]();
      const items = resp.results || [];

      // Fetch full reward data for items that have a reward ID
      const rewardsResp = await apiService.getLeaderboardRewards();
      const availableRewards = rewardsResp.results || rewardsResp || [];

      // Create a map of rewards by ID for quick lookup
      const rewardsMap = {};
      availableRewards.forEach(reward => {
        rewardsMap[reward.id] = reward;
      });

      // Populate reward objects for items that only have reward IDs
      const populatedItems = items.map(item => {
        if (item.reward && typeof item.reward === 'number') {
          // If reward is just an ID, populate it with the full reward object
          return {
            ...item,
            reward: rewardsMap[item.reward] || item.reward
          };
        }
        return item;
      });

      setItems(populatedItems);
    } catch (e) {
      setPageError(loadErrorLabel);
    } finally {
      setLoading(false);
    }
  }, [apiMethods.get, loadErrorLabel]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    const syncUser = async () => {
      const user = await apiService.fetchCurrentUser();
      setIsSuperuser(Boolean(user?.is_superuser));
    };
    syncUser();
  }, []);

  useEffect(() => {
    if (openItemMenu === null) return undefined;
    const onDocClick = event => {
      if (!event.target.closest(`[data-prize-menu="${openItemMenu}"]`)) {
        setOpenItemMenu(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [openItemMenu]);

  const onChange = key => e => {
    const value = key === 'is_active' ? e.target.checked : e.target.value;
    setFormError('');
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const resetForm = () => {
    setEditingId(null);
    setOverrideCoverTitle(false);
    setFormError('');
    setEditingRewardImages([]);
    setRemovedLeaderboardImageIds([]);
    setLeaderboardRewardImages([]);
    setLeaderboardImageInputKey(k => k + 1);
    const autoDates = getAutoDates();
    setForm({
      ...createEmptyForm(emptyFormArgs),
      ...autoDates,
    });
  };

  const onEdit = item => {
    setEditingId(item.id);
    setShowAddForm(true);
    setFormError('');
    // The prize serializer exposes the linked reward's gallery as `images`, so
    // the form can show what is already saved without a second fetch.
    setEditingRewardImages(Array.isArray(item.images) ? item.images.filter(img => img.id) : []);
    setRemovedLeaderboardImageIds([]);
    setLeaderboardRewardImages([]);
    setLeaderboardImageInputKey(k => k + 1);
    setOverrideCoverTitle(Boolean(item.cover_title && item.cover_title !== defaultCoverTitle));
    setForm({
      reward: item.reward || '',
      title: item.title || '',
      giveaway_type: item.giveaway_type || 'gift_card',
      description: item.description || '',
      how_to_win: item.how_to_win || '',
      location: item.location || '',
      terms: item.terms || '',
      cover_title: item.cover_title || defaultCoverTitle,
      cover_image: item.cover_image_url || item.cover_image || '',
      // Pickers work in the admin's own timezone; submit converts back to UTC.
      [dateStartKey]: itemLocalDate(item, dateStartAtKey, dateStartKey),
      [dateEndKey]: itemLocalDate(item, dateEndAtKey, dateEndKey),
      [dateStartTimeKey]:
        itemLocalTime(item, dateStartAtKey, dateStartTimeKey, dateStartKey) || defaultStartTime,
      [dateEndTimeKey]:
        itemLocalTime(item, dateEndAtKey, dateEndTimeKey, dateEndKey) || defaultEndTime,
      is_active: item.is_active !== false,
    });
  };

  const onDelete = async id => {
    if (!window.confirm(deleteConfirmLabel)) return;
    try {
      await apiService[apiMethods.delete](id);
      setItems(prev => prev.filter(x => x.id !== id));
      if (editingId === id) resetForm();
    } catch (e) {
      setPageError(deleteErrorLabel);
    }
  };

  const hasDateOverlap = (startMs, endMs, excludeId = null) => {
    return items.some(item => {
      if (excludeId && String(item.id) === String(excludeId)) return false;
      const itemStartMs = getRangeStartMs(item);
      const itemEndMs = getRangeEndMs(item);
      if (itemStartMs === null || itemEndMs === null) return false;
      return startMs < itemEndMs && endMs > itemStartMs;
    });
  };

  const onSubmit = async e => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      setFormError('Add a title and description for the reward.');
      return;
    }
    if (!form[dateStartKey] || !form[dateEndKey]) {
      setFormError(startEndRequiredError);
      return;
    }
    if (isSuperuser && (!form[dateStartTimeKey] || !form[dateEndTimeKey])) {
      setFormError('Set both start and end times.');
      return;
    }
    const existingItem = editingId ? items.find(x => x.id === editingId) : null;
    const submitStartTime = getSubmitStartTime(existingItem);
    const submitEndTime = getSubmitEndTime(existingItem);
    const rangeStartMs = new Date(
      combineDateTime(form[dateStartKey], submitStartTime, defaultStartTime)
    ).getTime();
    const rangeEndMs = new Date(
      combineDateTime(form[dateEndKey], submitEndTime, defaultEndTime)
    ).getTime();
    if (rangeEndMs < rangeStartMs) {
      setFormError(endBeforeStartError);
      return;
    }
    if (hasDateOverlap(rangeStartMs, rangeEndMs, editingId)) {
      setFormError(overlapError);
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      let prizePayload = { ...form };
      // The authoritative write: what the admin picked, read as their local time
      // and sent as a UTC instant. The naive keys still ride along below so an
      // older server (or the mirror columns) stay correct.
      prizePayload[dateStartAtKey] = instantFromLocal(
        form[dateStartKey],
        submitStartTime,
        defaultStartTime
      );
      prizePayload[dateEndAtKey] = instantFromLocal(
        form[dateEndKey],
        submitEndTime,
        defaultEndTime
      );
      if (usesCombinedDateTime) {
        prizePayload = {
          ...prizePayload,
          [dateStartKey]: combineDateTime(form[dateStartKey], submitStartTime, defaultStartTime),
          [dateEndKey]: combineDateTime(form[dateEndKey], submitEndTime, defaultEndTime),
        };
        delete prizePayload[dateStartTimeKey];
        delete prizePayload[dateEndTimeKey];
      } else if (!isSuperuser) {
        delete prizePayload[dateStartTimeKey];
        delete prizePayload[dateEndTimeKey];
        if (!editingId) {
          prizePayload[dateStartTimeKey] = defaultStartTime;
          prizePayload[dateEndTimeKey] = defaultEndTime;
        }
      }
      // Every prize owns a reward record — the gallery lives there, since a prize
      // has no image relation of its own. Adding creates one; editing writes back
      // to the one this prize already points at rather than spawning a duplicate.
      const lbPayload = {
        title: form.title,
        giveaway_type: form.giveaway_type,
        description: form.description,
        terms: form.terms,
        cover_title: form.cover_title,
        is_active: true,
      };
      // loadItems can swap `reward` for the whole reward object, so accept either.
      const linkedReward = existingItem?.reward;
      const linkedRewardId =
        linkedReward && typeof linkedReward === 'object' ? linkedReward.id : linkedReward || null;
      if (editingId && linkedRewardId) {
        await apiService.updateLeaderboardReward(
          linkedRewardId,
          lbPayload,
          leaderboardRewardImages,
          removedLeaderboardImageIds
        );
      } else {
        const createdReward = await apiService.createLeaderboardReward(
          lbPayload,
          leaderboardRewardImages
        );
        prizePayload = { ...prizePayload, reward: createdReward.id };
      }
      if (editingId) {
        const updated = await apiService[apiMethods.update](editingId, prizePayload);
        setItems(prev => prev.map(x => (x.id === updated.id ? updated : x)));
      } else {
        const created = await apiService[apiMethods.create](prizePayload);
        setItems(prev => [created, ...prev]);
      }
      resetForm();
      setShowAddForm(false);
    } catch (err) {
      setFormError(formatApiError(err?.response?.data, saveErrorLabel));
    } finally {
      setSaving(false);
    }
  };

  const handleAddClick = () => {
    setEditingId(null);
    setOverrideCoverTitle(false);
    setFormError('');
    setEditingRewardImages([]);
    setRemovedLeaderboardImageIds([]);
    setLeaderboardRewardImages([]);
    setLeaderboardImageInputKey(k => k + 1);
    const autoDates = getAutoDates();
    setForm({
      ...createEmptyForm(emptyFormArgs),
      ...autoDates,
    });
    setShowAddForm(true);
  };

  const handleCancel = () => {
    setShowAddForm(false);
    resetForm();
  };

  const leaderboardScope = apiMethods.get === 'getOverallPrizes' ? 'overall' : 'weekly';

  const loadPrizeLeaderboard = async (prizeId, options = {}) => {
    const { userId = null, force = false } = options;
    const hasCached = Boolean(prizeLeaderboards[prizeId]);
    if (!force && !userId && hasCached) {
      return prizeLeaderboards[prizeId];
    }

    setPrizeLeaderboardLoading(prev => ({ ...prev, [prizeId]: true }));
    setPrizeLeaderboardErrors(prev => ({ ...prev, [prizeId]: '' }));
    try {
      const params = userId ? { user_id: userId } : {};
      const response = leaderboardScope === 'overall'
        ? await apiService.getOverallPrizeLeaderboard(prizeId, params)
        : await apiService.getWeeklyPrizeLeaderboard(prizeId, params);

      if (response?.leaderboard) {
        setPrizeLeaderboards(prev => ({
          ...prev,
          [prizeId]: {
            leaderboard: response.leaderboard || [],
            period: response.period || null,
          },
        }));
      }
      return response;
    } catch (e) {
      const message = e?.response?.data?.error || 'Failed to load leaderboard.';
      setPrizeLeaderboardErrors(prev => ({ ...prev, [prizeId]: message }));
      throw e;
    } finally {
      setPrizeLeaderboardLoading(prev => ({ ...prev, [prizeId]: false }));
    }
  };

  const togglePrizeLeaderboard = async item => {
    const nextExpanded = !expandedLeaderboardItems[item.id];
    setExpandedLeaderboardItems(prev => ({ ...prev, [item.id]: nextExpanded }));
    if (nextExpanded) {
      try {
        await loadPrizeLeaderboard(item.id);
      } catch (_e) {
      }
    }
  };

  const openParticipantModal = async (item, row) => {
    setParticipantModal({
      open: true,
      prizeId: item.id,
      prizeTitle: item.title || defaultCoverTitle,
      participant: null,
      period: prizeLeaderboards[item.id]?.period || null,
    });
    setParticipantError('');
    setParticipantLoading(true);
    try {
      const response = await loadPrizeLeaderboard(item.id, { userId: row.id, force: true });
      setParticipantModal(prev => ({
        ...prev,
        participant: response?.participant || null,
        period: response?.period || prev.period,
      }));
    } catch (e) {
      const message = e?.response?.data?.error || 'Failed to load participant details.';
      setParticipantError(message);
    } finally {
      setParticipantLoading(false);
    }
  };

  const closeParticipantModal = () => {
    setParticipantModal({
      open: false,
      prizeId: null,
      prizeTitle: '',
      participant: null,
      period: null,
    });
    setParticipantError('');
    setParticipantLoading(false);
  };

  const formatDateTimeValue = value => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleString();
  };

  const renderPrizeLeaderboard = item => {
    if (!expandedLeaderboardItems[item.id]) return null;
    const cached = prizeLeaderboards[item.id];
    const rows = cached?.leaderboard || [];
    const loadingLeaderboard = Boolean(prizeLeaderboardLoading[item.id]);
    const errorMessage = prizeLeaderboardErrors[item.id];
    const period = cached?.period;

    return (
      <div
        style={{
          marginTop: 10,
          background: '#FFFFFF',
          border: '1px solid #E5E7EB',
          borderRadius: 10,
          padding: 12,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, gap: 8, flexWrap: 'wrap' }}>
          <strong>Leaderboard</strong>
          <div style={{ fontSize: 12, opacity: 0.75 }}>
            {period?.starts_at ? formatDateTimeValue(period.starts_at) : '—'} - {period?.ends_at ? formatDateTimeValue(period.ends_at) : '—'}
          </div>
        </div>
        {errorMessage ? (
          <div style={{ color: '#b00020', marginBottom: 8 }}>{errorMessage}</div>
        ) : null}
        {loadingLeaderboard ? (
          <div style={{ opacity: 0.7 }}>Loading leaderboard...</div>
        ) : rows.length === 0 ? (
          <div style={{ opacity: 0.7 }}>No participants yet.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #E5E7EB' }}>Rank</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #E5E7EB' }}>Participant</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #E5E7EB' }}>Email</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #E5E7EB' }}>Points</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr
                    key={`${item.id}-${row.id}`}
                    onClick={() => openParticipantModal(item, row)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #F3F4F6' }}>{row.rank}</td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #F3F4F6' }}>{row.name}</td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #F3F4F6' }}>{row.email || '—'}</td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #F3F4F6' }}>{row.points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  /**
   * One prize as a full-width preview card, matching the reward cards on the
   * rewards page and the first-rewards editor: everything on the face of it,
   * with a ⋮ menu for the actions.
   */
  const renderRewardItemCard = (item, isCompleted = false) => {
    const status = getStatusMeta(item);
    const previewImages = getItemPreviewImages(item);
    const boardOpen = Boolean(expandedLeaderboardItems[item.id]);

    const detailRows = [
      ['Description', item.description],
      [startDateLabel, formatScheduleCell(item, dateStartKey, dateStartTimeKey, dateStartAtKey)],
      [endDateLabel, formatScheduleCell(item, dateEndKey, dateEndTimeKey, dateEndAtKey)],
      [
        'Winner',
        item.winner_name
          ? [
              item.winner_name,
              item.winner_email || 'No email',
              typeof item.winning_points === 'number'
                ? `${item.winning_points} pts`
                : 'Points not set',
            ].join(' · ')
          : '',
      ],
    ];

    return (
      <div
        key={item.id}
        style={{
          position: 'relative',
          border: '2px solid #d1d5db',
          borderRadius: '10px',
          padding: '22px 24px',
          backgroundColor: isCompleted ? '#fafafa' : 'white',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '8px',
                marginBottom: '4px',
              }}
            >
              <span
                style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '17px',
                  fontWeight: 600,
                  color: '#111827',
                }}
              >
                {item.title || defaultCoverTitle}
              </span>
              <span
                style={{
                  padding: '1px 7px',
                  borderRadius: '100px',
                  fontFamily: '"Baikal Book"',
                  fontSize: '11px',
                  fontWeight: 600,
                  background: status.bg,
                  color: status.color,
                }}
              >
                {status.label}
              </span>
            </div>
            <div
              style={{
                fontFamily: '"Baikal Book"',
                fontSize: '14px',
                color: '#6b7280',
              }}
            >
              {formatScheduleCell(item, dateStartKey, dateStartTimeKey, dateStartAtKey)} →{' '}
              {formatScheduleCell(item, dateEndKey, dateEndTimeKey, dateEndAtKey)}
            </div>
          </div>

          <div
            data-prize-menu={item.id}
            style={{ position: 'relative', flexShrink: 0 }}
          >
            <button
              type="button"
              onClick={() =>
                setOpenItemMenu(current => (current === item.id ? null : item.id))
              }
              style={{
                padding: '4px',
                backgroundColor: 'transparent',
                color: '#6b7280',
                border: 'none',
                borderRadius: '4px',
                fontFamily: '"Baikal Book"',
                fontSize: '24px',
                fontWeight: 'bold',
                cursor: 'pointer',
                width: '32px',
                height: '32px',
              }}
              title="Reward options"
            >
              ⋮
            </button>
            {openItemMenu === item.id && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '4px',
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000,
                  minWidth: '160px',
                }}
              >
                <button
                  type="button"
                  style={prizeMenuItem}
                  onClick={() => {
                    setOpenItemMenu(null);
                    onEdit(item);
                  }}
                >
                  Edit
                </button>
                <button
                  type="button"
                  style={{ ...prizeMenuItem, borderTop: '1px solid #e5e7eb' }}
                  onClick={() => {
                    setOpenItemMenu(null);
                    togglePrizeLeaderboard(item);
                  }}
                >
                  {boardOpen ? 'Hide leaderboard' : 'Leaderboard'}
                </button>
                <button
                  type="button"
                  style={{
                    ...prizeMenuItem,
                    borderTop: '1px solid #e5e7eb',
                    color: '#dc2626',
                  }}
                  onClick={() => {
                    setOpenItemMenu(null);
                    onDelete(item.id);
                  }}
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>

        {previewImages.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              marginTop: '18px',
            }}
          >
            {previewImages.map((url, index) => (
              <img
                key={`${url}-${index}`}
                src={url}
                alt=""
                title={index === 0 ? 'Shown first in the app' : `Image ${index + 1}`}
                style={{
                  width: '104px',
                  height: '104px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                }}
                onError={e => {
                  e.target.style.display = 'none';
                }}
              />
            ))}
          </div>
        )}

        <div style={{ marginTop: '18px' }}>
          {detailRows.map(([fieldLabel, value]) => (
            <div
              key={fieldLabel}
              style={{
                display: 'grid',
                gridTemplateColumns: '140px 1fr',
                gap: '14px',
                padding: '10px 0',
                borderTop: '1px solid #f3f4f6',
                alignItems: 'start',
              }}
            >
              <span
                style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#6b7280',
                  lineHeight: 1.6,
                }}
              >
                {fieldLabel}
              </span>
              <span
                style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  lineHeight: 1.6,
                  color: value && value !== '—' ? '#374151' : '#9ca3af',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {value || '—'}
              </span>
            </div>
          ))}
        </div>

        {renderPrizeLeaderboard(item)}
      </div>
    );
  };

  // Already-saved images for the reward being edited, each removable on its own.
  // New uploads append to these rather than replacing them.
  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection={activeSection} />
      <div className="admin-main">
        <div className="admin-content">
          <div className="staff-page-header-row">
            <div>
              <h1 className="staff-page-header">{pageTitle}</h1>
              <p className="staff-page-subheader">
                {pageSubtitle}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="staff-page-button" onClick={loadItems}>
                <span className="staff-page-button-text">Refresh</span>
              </button>
              {/* Same ghost button the first-rewards editor uses. */}
              <button type="button" style={ghostAddButton} onClick={handleAddClick}>
                + Add reward
              </button>
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: 10,
              marginBottom: 14,
              alignItems: 'center',
            }}
          >
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="form-input"
              placeholder="Search by reward, winner, email, or date"
            />
            <select
              className="form-select"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              style={{ minWidth: 180 }}
            >
              <option value="all">All statuses</option>
              <option value="live">Live</option>
              <option value="upcoming">Upcoming</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          {pageError ? (
            <div style={{ color: '#b00020', marginBottom: 16, whiteSpace: 'pre-wrap' }}>{pageError}</div>
          ) : null}

          {loading ? (
            <div>{loadingLabel}</div>
          ) : (
            <div>
              <h3 style={{ margin: '0 0 10px 0' }}>Active</h3>
              {/* Two per row, so each card sits at half the page width. */}
              <div style={prizeCardGrid}>
                {visibleActiveItems.map(item => renderRewardItemCard(item))}
              </div>
              {visibleActiveItems.length === 0 ? (
                <div style={{ opacity: 0.7, marginBottom: 14 }}>{emptyActiveLabel}</div>
              ) : null}
              <h3 style={{ margin: '12px 0 10px 0' }}>Completed</h3>
              <div style={prizeCardGrid}>
                {visibleCompletedItems.map(item => renderRewardItemCard(item, true))}
              </div>
              {visibleActiveItems.length === 0 && visibleCompletedItems.length === 0 ? (
                <div style={{ opacity: 0.7 }}>{emptyAllLabel}</div>
              ) : null}
              {visibleCompletedItems.length === 0 && visibleActiveItems.length > 0 ? (
                <div style={{ opacity: 0.7 }}>{emptyCompletedLabel}</div>
              ) : null}
            </div>
          )}
        </div>
      </div>

      {showAddForm ? (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '0',
              // Matches the first-rewards editor modal.
              width: '640px',
              maxWidth: '92%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div className="add-reward-section" style={{ margin: 0 }}>
              <h1 className="staff-page-header">
                {editingId ? formEditTitle : formCreateTitle}
              </h1>
              <p className="staff-page-subheader">
                {editingId
                  ? formEditSubtitle
                  : formCreateSubtitle}
              </p>

              {formError ? (
                <div style={{ color: '#b00020', marginBottom: 16, whiteSpace: 'pre-wrap' }}>
                  {formError}
                </div>
              ) : null}

              <form onSubmit={onSubmit} className="reward-form reward-form--compact">
                <div className="form-group">
                  <label className="form-label" htmlFor="staff-lb-reward-title">
                    Reward title
                  </label>
                  <input
                    id="staff-lb-reward-title"
                    type="text"
                    value={form.title}
                    onChange={onChange('title')}
                    className="form-input"
                    placeholder="Reward title"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="staff-lb-reward-giveaway-type">
                    Category
                  </label>
                  <select
                    id="staff-lb-reward-giveaway-type"
                    value={form.giveaway_type}
                    onChange={onChange('giveaway_type')}
                    className="form-select"
                    required
                  >
                    <option value="merchandise">Merchandise</option>
                    <option value="apparel">Apparel</option>
                    <option value="experience">Experience</option>
                    <option value="collectibles">Collectibles</option>
                    <option value="tickets">Event Tickets</option>
                    <option value="gift_card">Gift Card</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="staff-lb-reward-description">
                    Description
                  </label>
                  <textarea
                    id="staff-lb-reward-description"
                    value={form.description}
                    onChange={onChange('description')}
                    className="form-textarea"
                    rows={4}
                    placeholder="Reward description"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Images</label>
                  {savedRewardImages.length ? (
                    <div className="image-preview-container">
                      {savedRewardImages.map(img => (
                        <div key={img.id} className="image-preview-item">
                          <div className="preview-image-wrapper">
                            <img
                              src={getImageUrl(img.image_path)}
                              alt=""
                              className="preview-image"
                            />
                            <button
                              type="button"
                              className="remove-image-btn"
                              onClick={() =>
                                setRemovedLeaderboardImageIds(prev => [...prev, String(img.id)])
                              }
                            >
                              <img src={xmarkIcon} alt="Remove" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  <StaffRewardImageUpload
                    key={leaderboardImageInputKey}
                    images={leaderboardRewardImages}
                    setImages={setLeaderboardRewardImages}
                    inputId={`leaderboard-reward-image-upload-${leaderboardImageInputKey}`}
                    existingCount={savedRewardImages.length}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="staff-weekly-how-to-win">
                    How to Win
                  </label>
                  <textarea
                    id="staff-weekly-how-to-win"
                    value={form.how_to_win}
                    onChange={onChange('how_to_win')}
                    className="form-textarea"
                    rows={4}
                    placeholder="Explain how participants can win this leaderboard reward"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="staff-weekly-location">
                    Location
                  </label>
                  <input
                    id="staff-weekly-location"
                    type="text"
                    value={form.location}
                    onChange={onChange('location')}
                    className="form-input"
                    placeholder="Where the prize takes place, e.g. Milan Puskar Stadium"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="staff-weekly-terms">
                    Terms
                  </label>
                  <textarea
                    id="staff-weekly-terms"
                    value={form.terms}
                    onChange={onChange('terms')}
                    className="form-textarea"
                    rows={4}
                    placeholder={termsPlaceholder}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label" htmlFor={`staff-weekly-${dateStartKey}`}>
                      {startDateLabel}
                    </label>
                    <input
                      id={`staff-weekly-${dateStartKey}`}
                      type="date"
                      value={form[dateStartKey]}
                      onChange={onChange(dateStartKey)}
                      className="form-input"
                      required
                    />
                    {isSuperuser ? (
                      <>
                        <label
                          className="form-label"
                          htmlFor={`staff-weekly-${dateStartTimeKey}`}
                          style={{ marginTop: 8 }}
                        >
                          {startTimeLabel}
                        </label>
                        <input
                          id={`staff-weekly-${dateStartTimeKey}`}
                          type="time"
                          value={form[dateStartTimeKey]}
                          onChange={onChange(dateStartTimeKey)}
                          className="form-input"
                          required
                        />
                      </>
                    ) : null}
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor={`staff-weekly-${dateEndKey}`}>
                      {endDateLabel}
                    </label>
                    <input
                      id={`staff-weekly-${dateEndKey}`}
                      type="date"
                      value={form[dateEndKey]}
                      onChange={onChange(dateEndKey)}
                      className="form-input"
                      required
                    />
                    {isSuperuser ? (
                      <>
                        <label
                          className="form-label"
                          htmlFor={`staff-weekly-${dateEndTimeKey}`}
                          style={{ marginTop: 8 }}
                        >
                          {endTimeLabel}
                        </label>
                        <input
                          id={`staff-weekly-${dateEndTimeKey}`}
                          type="time"
                          value={form[dateEndTimeKey]}
                          onChange={onChange(dateEndTimeKey)}
                          className="form-input"
                          required
                        />
                      </>
                    ) : null}
                  </div>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <input
                      type="checkbox"
                      checked={overrideCoverTitle}
                      onChange={e => {
                        const checked = e.target.checked;
                        setOverrideCoverTitle(checked);
                        if (!checked) {
                          setForm(prev => ({ ...prev, cover_title: defaultCoverTitle }));
                        }
                      }}
                    />
                    Override coverTitle
                  </label>
                  <label className="form-label" htmlFor="staff-weekly-cover-title">
                    Custom cover title
                  </label>
                  <input
                    id="staff-weekly-cover-title"
                    type="text"
                    value={form.cover_title}
                    onChange={onChange('cover_title')}
                    className="form-input"
                    placeholder={coverTitlePlaceholder}
                    disabled={!overrideCoverTitle}
                  />
                </div>

                {editingId ? (
                  <div className="form-group">
                    <label className="form-label">Winner</label>
                    <div
                      style={{
                        border: '1px solid #E5E7EB',
                        borderRadius: 8,
                        padding: 12,
                        background: '#FAFAFA',
                        fontSize: 13,
                      }}
                    >
                      {sortedItems.find(x => x.id === editingId)?.winner_name ? (
                        <>
                          <div>
                            <strong>Name:</strong>{' '}
                            {sortedItems.find(x => x.id === editingId)?.winner_name}
                          </div>
                          <div>
                            <strong>Email:</strong>{' '}
                            {sortedItems.find(x => x.id === editingId)?.winner_email || '—'}
                          </div>
                          <div>
                            <strong>Points:</strong>{' '}
                            {typeof sortedItems.find(x => x.id === editingId)?.winning_points === 'number'
                              ? `${sortedItems.find(x => x.id === editingId)?.winning_points} pts`
                              : '—'}
                          </div>
                        </>
                      ) : (
                        <span style={{ opacity: 0.7 }}>No winner set yet.</span>
                      )}
                    </div>
                  </div>
                ) : null}

                <div className="form-group">
                  <label
                    className="form-label"
                    htmlFor="staff-weekly-is-active"
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <input
                      id="staff-weekly-is-active"
                      type="checkbox"
                      checked={form.is_active}
                      onChange={onChange('is_active')}
                    />
                    Active
                  </label>
                </div>

                <div className="form-actions">
                  <button type="button" className="cancel-button" onClick={handleCancel}>
                    Cancel
                  </button>
                  <button type="submit" className="staff-page-button submit-button" disabled={saving}>
                    <span className="staff-page-button-text">
                      {saving
                        ? 'Saving...'
                        : editingId
                          ? 'Update Reward'
                          : 'Create Reward'}
                    </span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}

      {participantModal.open ? (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1100,
          }}
          onClick={closeParticipantModal}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: 'min(960px, 92vw)',
              maxHeight: '88vh',
              overflowY: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
              padding: 20,
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
              <div>
                <h2 style={{ margin: 0 }}>{participantModal.prizeTitle}</h2>
                <div style={{ fontSize: 13, opacity: 0.7, marginTop: 6 }}>
                  {participantModal.period?.starts_at ? formatDateTimeValue(participantModal.period.starts_at) : '—'} - {participantModal.period?.ends_at ? formatDateTimeValue(participantModal.period.ends_at) : '—'}
                </div>
              </div>
              <button className="cancel-button" type="button" onClick={closeParticipantModal}>Close</button>
            </div>

            {participantError ? (
              <div style={{ color: '#b00020', marginBottom: 12 }}>{participantError}</div>
            ) : null}

            {participantLoading ? (
              <div style={{ opacity: 0.7 }}>Loading participant details...</div>
            ) : participantModal.participant ? (
              <div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: 10,
                    marginBottom: 14,
                  }}
                >
                  <div style={{ background: '#F8F8F8', borderRadius: 10, padding: 10 }}>
                    <div style={{ fontSize: 12, opacity: 0.7 }}>Participant</div>
                    <div style={{ fontWeight: 700 }}>{participantModal.participant.name}</div>
                    <div style={{ fontSize: 12, opacity: 0.8 }}>{participantModal.participant.email || '—'}</div>
                  </div>
                  <div style={{ background: '#F8F8F8', borderRadius: 10, padding: 10 }}>
                    <div style={{ fontSize: 12, opacity: 0.7 }}>Rank</div>
                    <div style={{ fontWeight: 700 }}>#{participantModal.participant.rank || '—'}</div>
                  </div>
                  <div style={{ background: '#F8F8F8', borderRadius: 10, padding: 10 }}>
                    <div style={{ fontSize: 12, opacity: 0.7 }}>Points</div>
                    <div style={{ fontWeight: 700 }}>{participantModal.participant.points || 0}</div>
                  </div>
                  <div style={{ background: '#F8F8F8', borderRadius: 10, padding: 10 }}>
                    <div style={{ fontSize: 12, opacity: 0.7 }}>Fan ID</div>
                    <div style={{ fontWeight: 700 }}>{participantModal.participant.fan_id || '—'}</div>
                  </div>
                </div>

                <div style={{ marginBottom: 14 }}>
                  <h3 style={{ margin: '0 0 8px 0' }}>Points by source</h3>
                  {participantModal.participant.source_breakdown?.length ? (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #E5E7EB' }}>Source</th>
                            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #E5E7EB' }}>Entries</th>
                            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #E5E7EB' }}>Points</th>
                          </tr>
                        </thead>
                        <tbody>
                          {participantModal.participant.source_breakdown.map(row => (
                            <tr key={`${row.source}-${row.points}-${row.entries}`}>
                              <td style={{ padding: '6px 8px', borderBottom: '1px solid #F3F4F6' }}>{row.source}</td>
                              <td style={{ padding: '6px 8px', borderBottom: '1px solid #F3F4F6' }}>{row.entries}</td>
                              <td style={{ padding: '6px 8px', borderBottom: '1px solid #F3F4F6' }}>{row.points}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{ opacity: 0.7 }}>No source data.</div>
                  )}
                </div>

                <div>
                  <h3 style={{ margin: '0 0 8px 0' }}>Transactions in period</h3>
                  {participantModal.participant.entries?.length ? (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #E5E7EB' }}>Date</th>
                            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #E5E7EB' }}>Source</th>
                            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #E5E7EB' }}>Merchant</th>
                            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #E5E7EB' }}>Description</th>
                            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #E5E7EB' }}>Points</th>
                          </tr>
                        </thead>
                        <tbody>
                          {participantModal.participant.entries.map(entry => (
                            <tr key={entry.id}>
                              <td style={{ padding: '6px 8px', borderBottom: '1px solid #F3F4F6' }}>{formatDateTimeValue(entry.created_at)}</td>
                              <td style={{ padding: '6px 8px', borderBottom: '1px solid #F3F4F6' }}>{entry.earning_source || 'other'}</td>
                              <td style={{ padding: '6px 8px', borderBottom: '1px solid #F3F4F6' }}>{entry.merchant_name || '—'}</td>
                              <td style={{ padding: '6px 8px', borderBottom: '1px solid #F3F4F6' }}>{entry.description || '—'}</td>
                              <td style={{ padding: '6px 8px', borderBottom: '1px solid #F3F4F6' }}>{entry.points}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{ opacity: 0.7 }}>No transactions in selected period.</div>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ opacity: 0.7 }}>Participant not found.</div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default StaffWeeklyRewards;
